import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recordingFolders } from '@/api/recordingFolders';
import type { RecordingFolderRow, RecordingFolderItemRow } from '@/api/recordingFolders';
import type { RecordingRow } from '@/api/recordings';
import { recordings as recordingsApi } from '@/api/recordings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useAuth } from '@/lib/auth-store';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { SmsListPicker } from '@/components/SmsListPicker';
import { RecordingPicker } from '@/components/ivr/RecordingPicker';
import { DateTimePicker } from '@/components/DateTimePicker';
import { NewRecordingMenu } from '@/components/recordings/NewRecordingMenu';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/cn';
import {
  Plus, Trash2, Clock, Pencil, Check, X, ChevronDown as ChevronDownIcon,
  ArrowLeft, FolderOpen, GripVertical, ChevronUp, ChevronDown, Play,
  ChevronLeft, ChevronRight, CalendarClock, FolderMinus, Download,
} from 'lucide-react';

// Page-size options for the recordings list — same shape as the folder
// popup and AuthListDetailPage, default 50.
const FOLDER_PAGE_SIZES = [50, 100, 200, 'ALL'] as const;
type FolderPageSize = typeof FOLDER_PAGE_SIZES[number];

const MUTATE_ROLES = ['super_admin', 'reseller_owner', 'office_manager', 'standard_user'];

function durationLabel(ms: number | null): string {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

// ── Folder-item scheduling helpers ────────────────────────────────────────────
//
// Each folder item may carry a go-live (available_from) and expire
// (available_until) timestamp. When "now" is outside that window the call
// ENGINE skips the item (not played, not counted) — but the portal keeps
// showing it here so the customer can manage it. NULL on either bound = no
// bound. See migration 0111.

// The in-house DateTimePicker emits a local-wall-clock string with no tz
// suffix (e.g. "2026-06-18T14:30") — same shape the native datetime-local
// used. We store UTC. These two helpers convert between the picker's
// local-wall-clock string and the UTC ISO string the API stores.

/** UTC ISO (or null) → value for <input type="datetime-local"> in local time. */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Build YYYY-MM-DDTHH:mm from LOCAL components (getFullYear/getMonth/... are
  // local-time getters), so the field shows the moment in the user's own zone.
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** datetime-local value (local wall-clock, no tz) → UTC ISO, or null if empty. */
function localInputToIso(local: string): string | null {
  if (!local.trim()) return null;
  // new Date("2026-06-18T14:30") parses as LOCAL time per the JS spec for
  // date-time strings without a timezone offset; toISOString() emits UTC (Z).
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

type ItemAvailability = 'live' | 'scheduled' | 'expired';

/** Compute an item's availability state from its window vs the current time. */
function availabilityStatus(
  from: string | null,
  until: string | null,
  now: number = Date.now(),
): ItemAvailability {
  if (from) {
    const f = new Date(from).getTime();
    if (!Number.isNaN(f) && now < f) return 'scheduled';
  }
  if (until) {
    const u = new Date(until).getTime();
    if (!Number.isNaN(u) && now >= u) return 'expired';
  }
  return 'live';
}

/** True when the item carries any scheduling bound at all. */
function hasSchedule(from: string | null, until: string | null): boolean {
  return Boolean(from) || Boolean(until);
}

function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ── Per-item status chip ──────────────────────────────────────────────────────
// "Scheduled" (amber) before go-live, "Expired" (slate, struck) after expire,
// nothing when always-on, and a subtle "Scheduled window" hint otherwise.

function ItemStatusChip({ from, until }: { from: string | null; until: string | null }) {
  if (!hasSchedule(from, until)) return null;
  const status = availabilityStatus(from, until);
  if (status === 'scheduled') {
    return (
      <span
        className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
        title={`Goes live ${fmtWhen(from)}`}
      >
        Scheduled
      </span>
    );
  }
  if (status === 'expired') {
    return (
      <span
        className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 ring-1 ring-inset ring-slate-200 line-through"
        title={`Expired ${fmtWhen(until)}`}
      >
        Expired
      </span>
    );
  }
  // Live, but a window is set — show a quiet green hint so the user knows the
  // item is on a schedule even while it's currently playable.
  return (
    <span
      className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200"
      title={`Live${until ? ` until ${fmtWhen(until)}` : ''}`}
    >
      Live
    </span>
  );
}

// ── Inline audio player for a single recording row ───────────────────────────
// Used inline on the SAME line as the play button so opening a recording's
// player doesn't double the row height. Compact mode keeps the player at ~22px
// tall, which matches the row's natural height when only the name is shown.

function AudioPlayerInRow({ recordingId }: { recordingId: string }) {
  const accessToken = useAuth((s) => s.accessToken);
  const src = recordingsApi.audioUrl(recordingId, accessToken);
  return <AudioPlayer src={src} compact />;
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('animate-spin', className)} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

// ── Root page ─────────────────────────────────────────────────────────────────

export function FolderDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: folder, isLoading, error } = useQuery({
    queryKey: ['folder', id],
    queryFn: () => recordingFolders.get(id!),
    enabled: Boolean(id),
  });

  if (isLoading) {
    return <div className="text-sm text-slate-400">Loading…</div>;
  }

  if (error || !folder) {
    return (
      <div className="text-sm text-red-500">
        Folder not found.{' '}
        <Link to="/folders" className="underline">Back to Folders</Link>
      </div>
    );
  }

  return <FolderDetailInner folder={folder} />;
}

// ── Inner component (receives loaded folder) ──────────────────────────────────
//
// Exported so the master-detail view on /folders can render this whole
// shell in its right pane (Sam 2026-05-20). When `embedded` is true the
// back link and CustomerScopeChip are skipped because the master page
// already provides them.

export function FolderDetailInner({
  folder,
  embedded = false,
}: {
  folder: RecordingFolderRow;
  embedded?: boolean;
}) {
  const user = useCurrentUser();
  const canMutate = MUTATE_ROLES.includes(user.role);
  const effectiveCustomerId = useEffectiveCustomerId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // ── Bulk selection (checkbox per row + select-all-on-page) ────────────
  // Keyed by folder-ITEM id (unique per row); recording ids are derived
  // when an action needs them (ZIP / delete).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<null | 'zip' | 'remove' | 'delete'>(null);
  const [confirmBulk, setConfirmBulk] = useState<null | 'remove' | 'delete'>(null);

  // Re-fetch the folder on every mutation so the header stays current.
  function invalidateFolder() {
    qc.invalidateQueries({ queryKey: ['folder', folder.id] });
    qc.invalidateQueries({ queryKey: ['allFolders', effectiveCustomerId] });
  }

  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ['folderItems', folder.id],
    queryFn: () => recordingFolders.listItems(folder.id),
  });

  const rawItems = itemsData?.rows ?? [];
  // Optimistic order state for up/down reorder
  const [localOrder, setLocalOrder] = useState<RecordingFolderItemRow[] | null>(null);
  const items = localOrder ?? [...rawItems].sort((a, b) => a.sort_order - b.sort_order);

  // ── Pagination (client-side; API returns the full list) ───────────────
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<FolderPageSize>(50);
  const pageCount = pageSize === 'ALL'
    ? 1
    : Math.max(1, Math.ceil(items.length / pageSize));
  const startIdx = pageSize === 'ALL' ? 0 : page * pageSize;
  const endIdx = pageSize === 'ALL' ? items.length : Math.min(startIdx + pageSize, items.length);
  const visibleItems = items.slice(startIdx, endIdx);
  const showingFrom = items.length === 0 ? 0 : startIdx + 1;
  const showingTo = endIdx;
  // Clamp page if items shrink (e.g. removing the last item on the last page).
  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  // Prune selection when items disappear underneath it (removed on another
  // page, deleted elsewhere, folder refetch) so "N selected" stays honest.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const alive = new Set(rawItems.map((i) => i.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (alive.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [rawItems]);

  const selectedItems = items.filter((i) => selectedIds.has(i.id));
  const selectedCount = selectedItems.length;

  function toggleSelect(itemId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  function togglePageSelection(select: boolean, pageItems: RecordingFolderItemRow[]) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const it of pageItems) {
        if (select) next.add(it.id);
        else next.delete(it.id);
      }
      return next;
    });
  }

  const remove = useMutation({
    mutationFn: (itemId: string) => recordingFolders.removeItem(itemId),
    onSuccess: () => {
      setLocalOrder(null);
      qc.invalidateQueries({ queryKey: ['folderItems', folder.id] });
      // Removing from a folder is NOT a delete — say so, every time.
      toast({
        title: 'Removed from folder',
        description: 'The recording itself was not deleted.',
      });
    },
  });

  // ── Bulk actions ──────────────────────────────────────────────────────

  async function handleBulkZip() {
    // A recording can only appear once per folder (DB unique), but Set it
    // anyway so the endpoint never sees duplicates.
    const recIds = [...new Set(selectedItems.map((i) => i.recording_id))];
    if (recIds.length === 0) return;
    setBulkBusy('zip');
    try {
      const blob = await recordingsApi.downloadZip(recIds);
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recordings-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setSelectedIds(new Set());
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err instanceof Error ? err.message : 'Could not prepare the ZIP.',
        variant: 'destructive',
      });
    } finally {
      setBulkBusy(null);
    }
  }

  async function handleBulkRemove() {
    const ids = selectedItems.map((i) => i.id);
    setConfirmBulk(null);
    if (ids.length === 0) return;
    setBulkBusy('remove');
    try {
      const results = await Promise.allSettled(
        ids.map((id) => recordingFolders.removeItem(id)),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = ids.length - failed;
      if (failed > 0) {
        toast({
          title: `${ok} removed, ${failed} failed`,
          description: 'Some recordings could not be removed from the folder. They are still in your library either way.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: `Removed ${ok} from folder`,
          description: 'The recordings themselves were not deleted.',
        });
      }
    } finally {
      setBulkBusy(null);
      setSelectedIds(new Set());
      setLocalOrder(null);
      qc.invalidateQueries({ queryKey: ['folderItems', folder.id] });
    }
  }

  async function handleBulkDelete() {
    const recIds = [...new Set(selectedItems.map((i) => i.recording_id))];
    setConfirmBulk(null);
    if (recIds.length === 0) return;
    setBulkBusy('delete');
    try {
      const results = await Promise.allSettled(
        recIds.map((id) => recordingsApi.remove(id)),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = recIds.length - failed;
      if (failed > 0) {
        toast({
          title: `${ok} deleted, ${failed} failed`,
          description: 'Some recordings could not be deleted.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: ok === 1
            ? 'Recording moved to the Recycle Bin'
            : `${ok} recordings moved to the Recycle Bin`,
        });
      }
    } finally {
      setBulkBusy(null);
      setSelectedIds(new Set());
      setLocalOrder(null);
      // Soft-deleting a recording hides its folder items automatically —
      // listFolderItems INNER JOINs recordings ON deleted_at IS NULL — so
      // refetching the items list is all the cleanup this view needs.
      qc.invalidateQueries({ queryKey: ['folderItems', folder.id] });
      qc.invalidateQueries({ queryKey: ['recordings'] });
      invalidateFolder();
    }
  }

  // Set / clear an item's scheduling window. The popover sends already-
  // converted UTC ISO strings (or null to clear). On success we refresh the
  // items query so the status chip re-computes.
  const schedule = useMutation({
    mutationFn: (vars: {
      itemId: string;
      available_from: string | null;
      available_until: string | null;
    }) =>
      recordingFolders.updateItem(vars.itemId, {
        available_from: vars.available_from,
        available_until: vars.available_until,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folderItems', folder.id] });
    },
  });

  const reorder = useMutation({
    mutationFn: (itemIds: string[]) => recordingFolders.reorderItems(folder.id, itemIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folderItems', folder.id] });
    },
    onError: () => {
      setLocalOrder(null);
    },
  });

  // moveItem operates on GLOBAL indices so drag-drop continues to work
  // when the list is paginated. FolderItemsTable receives only the
  // visible slice + a startIdx offset; it adjusts its drag handler to
  // pass globalIdx values up.
  function moveItem(fromIndex: number, toIndex: number) {
    if (toIndex < 0 || toIndex >= items.length) return;
    const next = items.slice();
    const [moved] = next.splice(fromIndex, 1);
    if (!moved) return;
    next.splice(toIndex, 0, moved);
    setLocalOrder(next);
    reorder.mutate(next.map((i) => i.id));
  }

  // Multi-file uploads fire onCreated once per file in quick succession —
  // chain the folder adds so they land in upload order (and one failure
  // doesn't wedge the chain).
  const addChainRef = useRef<Promise<void>>(Promise.resolve());
  function handleRecordingCreated(recording: RecordingRow) {
    addChainRef.current = addChainRef.current
      .then(() => recordingFolders.addItem(folder.id, { recording_id: recording.id }))
      .then(
        () => { qc.invalidateQueries({ queryKey: ['folderItems', folder.id] }); },
        () => undefined, // already-in-folder / transient failure — skip quietly
      );
  }

  return (
    <div className="space-y-5">
      {/* Back link + header */}
      <div>
        {!embedded && (
          <Link
            to="/folders"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            All folders
          </Link>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <FolderOpen className="h-5 w-5 text-slate-400 shrink-0" />
          <InlineFolderName folder={folder} canMutate={canMutate} onSaved={invalidateFolder} />
        </div>
        {/* Folder description is shown read-only here when set; editing
            lives in the Folder Settings card below. Sam asked for the
            description edit to sit next to the Name edit inside Folder
            Settings rather than as a separate top-of-page widget. */}
        {folder.description?.trim() && (
          <p className="text-sm text-slate-500 whitespace-pre-wrap leading-snug">
            {folder.description}
          </p>
        )}

        {!embedded && <CustomerScopeChip />}
      </div>

      {/* Recordings card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Recordings</CardTitle>
            <CardDescription>
              {folder.description || `${items.length} recording${items.length !== 1 ? 's' : ''} in this folder`}
            </CardDescription>
          </div>
          {canMutate && (
            <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
              <PopoverTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add recordings
                  <ChevronDownIcon className="h-3.5 w-3.5 ml-1 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0 overflow-hidden">
                <div className="px-3 pt-3 pb-2">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
                    From library
                  </p>
                  <AddExistingInline
                    folderId={folder.id}
                    onAdded={() => {
                      qc.invalidateQueries({ queryKey: ['folderItems', folder.id] });
                      setAddPopoverOpen(false);
                    }}
                  />
                </div>
                <NewRecordingMenu
                  folderId={folder.id}
                  customerId={effectiveCustomerId}
                  onCreated={(recording) => {
                    setAddPopoverOpen(false);
                    handleRecordingCreated(recording);
                  }}
                  compact
                  multipleUpload
                />
              </PopoverContent>
            </Popover>
          )}
        </CardHeader>
        <CardContent>
          {itemsLoading ? (
            <div className="text-sm text-slate-400">Loading items…</div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg p-10 text-center text-sm text-muted-foreground bg-secondary/20">
              No recordings in this folder yet. Use &ldquo;Add recordings&rdquo; to get started.
            </div>
          ) : (
            <>
              <FolderItemsTable
                items={visibleItems}
                startIdx={startIdx}
                totalCount={items.length}
                canMutate={canMutate}
                onRemove={(id) => remove.mutate(id)}
                removePending={remove.isPending}
                onMove={moveItem}
                onSchedule={(itemId, available_from, available_until) =>
                  schedule.mutate({ itemId, available_from, available_until })}
                schedulePending={schedule.isPending}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onTogglePageSelection={(select) => togglePageSelection(select, visibleItems)}
              />

              {/* Bulk-action bar — floats above the viewport bottom while
                  the list is in view so the actions stay reachable on long
                  folders. Appears as soon as one row is checked. */}
              {canMutate && selectedCount > 0 && (
                <div className="sticky bottom-3 z-20 mt-3">
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 shadow-lg">
                    <span className="text-xs font-medium text-slate-700 tabular-nums">
                      {selectedCount} selected
                    </span>
                    <div className="ml-auto flex flex-wrap items-center gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={bulkBusy !== null}
                        onClick={handleBulkZip}
                      >
                        {bulkBusy === 'zip'
                          ? <SpinnerIcon className="h-3.5 w-3.5 mr-1.5" />
                          : <Download className="h-3.5 w-3.5 mr-1.5" />}
                        {bulkBusy === 'zip' ? 'Preparing…' : 'Download ZIP'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={bulkBusy !== null}
                        onClick={() => setConfirmBulk('remove')}
                      >
                        <FolderMinus className="h-3.5 w-3.5 mr-1.5" />
                        Remove from folder
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={bulkBusy !== null}
                        className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                        onClick={() => setConfirmBulk('delete')}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Delete recordings
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={bulkBusy !== null}
                        onClick={() => setSelectedIds(new Set())}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Pagination footer — same shape as AuthListDetailPage. */}
              <div className="flex items-center justify-between gap-3 flex-wrap text-xs pt-3 mt-3 border-t border-border">
                <div className="text-muted-foreground">
                  Showing {showingFrom}–{showingTo} of {items.length}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPageSize(v === 'ALL' ? 'ALL' : (Number(v) as FolderPageSize));
                      setPage(0);
                    }}
                    className="rounded border border-input bg-background px-2 py-1 text-xs"
                  >
                    {FOLDER_PAGE_SIZES.map((n) => (
                      <option key={String(n)} value={String(n)}>{n}</option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page === 0 || pageSize === 'ALL'}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span className="font-mono">
                    Page {page + 1} of {pageCount}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page + 1 >= pageCount || pageSize === 'ALL'}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Folder settings (collapsible) */}
      <Card>
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setSettingsOpen((v) => !v)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Folder settings</CardTitle>
            {settingsOpen
              ? <ChevronUp className="h-4 w-4 text-slate-400" />
              : <ChevronDown className="h-4 w-4 text-slate-400" />
            }
          </div>
        </CardHeader>
        {settingsOpen && (
          <CardContent>
            <FolderSettings
              folder={folder}
              canMutate={canMutate}
              onSaved={invalidateFolder}
              onDeleted={() => navigate('/folders')}
            />
          </CardContent>
        )}
      </Card>

      {/* Bulk remove-from-folder confirmation */}
      <Dialog open={confirmBulk === 'remove'} onOpenChange={(v) => { if (!v) setConfirmBulk(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove from folder?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Remove {selectedCount} recording{selectedCount !== 1 ? 's' : ''} from this
            folder? The recordings themselves are not deleted — they stay in your
            library.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="button" onClick={handleBulkRemove}>
              Remove from folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={confirmBulk === 'delete'} onOpenChange={(v) => { if (!v) setConfirmBulk(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete recordings?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Delete {selectedCount} recording{selectedCount !== 1 ? 's' : ''}? They move
            to the Recycle Bin and disappear from every folder that uses them.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="button" variant="destructive" onClick={handleBulkDelete}>
              Delete recordings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Inline folder name editor ─────────────────────────────────────────────────

function InlineFolderName({
  folder, canMutate, onSaved,
}: {
  folder: RecordingFolderRow;
  canMutate: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(folder.name);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const update = useMutation({
    mutationFn: (name: string) => recordingFolders.update(folder.id, { name }),
    onSuccess: () => {
      setEditing(false);
      setError('');
      onSaved();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Update failed.');
    },
  });

  function startEdit() {
    setDraft(folder.name);
    setError('');
    setEditing(true);
    setTimeout(() => { inputRef.current?.select(); }, 30);
  }

  function commit() {
    const trimmed = draft.trim();
    if (trimmed === folder.name || !trimmed) {
      setEditing(false);
      return;
    }
    update.mutate(trimmed);
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <h1 className="text-lg font-semibold">{folder.name}</h1>
        {canMutate && (
          <button
            type="button"
            onClick={startEdit}
            className="rounded p-0.5 text-slate-300 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Rename folder"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => { setDraft(e.target.value); setError(''); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        onBlur={commit}
        className="rounded border border-slate-300 px-2 py-0.5 text-lg font-semibold focus:border-slate-500 focus:outline-none"
        autoFocus
        aria-label="Folder name"
      />
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); commit(); }}
        className="rounded p-0.5 text-green-600 hover:bg-green-50"
        aria-label="Save"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setEditing(false); }}
        className="rounded p-0.5 text-slate-400 hover:bg-slate-100"
        aria-label="Cancel"
      >
        <X className="h-4 w-4" />
      </button>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
}

// ── Items table ───────────────────────────────────────────────────────────────

function FolderItemsTable({
  items, startIdx, totalCount, canMutate, onRemove, removePending, onMove,
  onSchedule, schedulePending,
  selectedIds, onToggleSelect, onTogglePageSelection,
}: {
  /** Visible page slice. */
  items: RecordingFolderItemRow[];
  /** Offset of the slice within the full list — used to display the
   *  global position number and to translate page-local drag indices
   *  into global indices for onMove. */
  startIdx: number;
  /** Total item count (for the helper line at the bottom). */
  totalCount: number;
  canMutate: boolean;
  onRemove: (id: string) => void;
  removePending: boolean;
  onMove: (from: number, to: number) => void;
  /** Persist a new scheduling window for an item (UTC ISO strings or null). */
  onSchedule: (itemId: string, availableFrom: string | null, availableUntil: string | null) => void;
  schedulePending: boolean;
  /** Bulk selection — item ids currently checked. */
  selectedIds: Set<string>;
  onToggleSelect: (itemId: string) => void;
  /** Check / uncheck every row on this page. */
  onTogglePageSelection: (select: boolean) => void;
}) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  const selectedOnPage = items.filter((i) => selectedIds.has(i.id)).length;
  const pageAllSelected: boolean | 'indeterminate' =
    items.length > 0 && selectedOnPage === items.length
      ? true
      : selectedOnPage > 0
        ? 'indeterminate'
        : false;

  // Single-bordered container — the parent CardContent already provides
  // the visual frame; no nested rounded-lg/border around the table.
  return (
    <div className="overflow-hidden">
      {/* Column headers */}
      <div className="grid grid-cols-[2.5rem_1.75rem_2rem_1fr_6rem_5rem_auto] gap-0 border-b border-border bg-slate-50/60">
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500" />
        <div className="py-2 flex items-center justify-center">
          {canMutate && (
            <Checkbox
              checked={pageAllSelected}
              onCheckedChange={(v) => onTogglePageSelection(v === true)}
              aria-label="Select all recordings on this page"
            />
          )}
        </div>
        <div className="py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-right pr-1">#</div>
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Recording</div>
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <Clock className="h-3 w-3" /> Duration
        </div>
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Schedule</div>
        <div />
      </div>

      <ul className="divide-y divide-border">
        {items.map((item, i) => {
          const globalIdx = startIdx + i;
          return (
            <FolderItemRow
              key={item.id}
              item={item}
              index={globalIdx}
              canMutate={canMutate}
              onRemove={() => onRemove(item.id)}
              removePending={removePending}
              onSchedule={onSchedule}
              schedulePending={schedulePending}
              isDragOver={overIdx === i && dragIdx !== i}
              draggable={canMutate}
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
              onDragLeave={() => setOverIdx(null)}
              onDrop={() => {
                if (dragIdx !== null && dragIdx !== i) {
                  // Translate page-local indices into global ones.
                  onMove(startIdx + dragIdx, startIdx + i);
                }
                setDragIdx(null); setOverIdx(null);
              }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              isPlayerOpen={openItemId === item.id}
              onTogglePlayer={() => setOpenItemId(openItemId === item.id ? null : item.id)}
              selected={selectedIds.has(item.id)}
              onToggleSelect={() => onToggleSelect(item.id)}
            />
          );
        })}
      </ul>
      <p className="px-3 py-1.5 text-xs text-muted-foreground bg-slate-50/40">
        Numbers shown are what callers press in Series nodes.
        {totalCount > items.length && ` Showing ${items.length} of ${totalCount}.`}
      </p>
    </div>
  );
}

// ── Single item row with drag-and-drop reorder ────────────────────────────────

function FolderItemRow({
  item, index, canMutate, onRemove, removePending,
  onSchedule, schedulePending,
  isDragOver, draggable, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
  isPlayerOpen, onTogglePlayer,
  selected, onToggleSelect,
}: {
  item: RecordingFolderItemRow;
  index: number;
  canMutate: boolean;
  onRemove: () => void;
  removePending: boolean;
  onSchedule: (itemId: string, availableFrom: string | null, availableUntil: string | null) => void;
  schedulePending: boolean;
  isDragOver: boolean;
  draggable: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
  isPlayerOpen: boolean;
  onTogglePlayer: () => void;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const scheduled = hasSchedule(item.available_from, item.available_until);
  return (
    <li
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={cn(
        'grid grid-cols-[2.5rem_1.75rem_2rem_1fr_6rem_5rem_auto] gap-0 items-center text-sm transition-colors hover:bg-slate-50/60',
        isDragOver ? 'bg-indigo-50 border-t-2 border-indigo-400' : '',
        selected ? 'bg-indigo-50/40' : '',
      )}
    >
      {/* Drag handle */}
      <div className="py-2.5 flex items-center justify-center">
        {canMutate ? (
          <span className="cursor-grab text-slate-300 hover:text-slate-500 px-1 flex items-center justify-center">
            <GripVertical className="h-4 w-4" />
          </span>
        ) : null}
      </div>

      {/* Bulk-select checkbox */}
      <div className="py-2.5 flex items-center justify-center">
        {canMutate && (
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelect}
            aria-label={`Select ${item.recording_name || 'recording'}`}
          />
        )}
      </div>

      {/* Position number */}
      <div className="py-2.5 flex items-center justify-end pr-1">
        <span className="text-xs font-mono text-muted-foreground tabular-nums w-6 text-right leading-none">
          {index + 1}
        </span>
      </div>

      {/* Recording name + inline player. When open, the compact player sits
          on the SAME line to the right of the name (truncated harder) so the
          row only grows by a few px instead of doubling height. */}
      <div className="px-3 py-2 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            type="button"
            onClick={onTogglePlayer}
            className="shrink-0 h-6 w-6 rounded flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
            aria-label={isPlayerOpen ? 'Close player' : 'Play preview'}
          >
            {isPlayerOpen
              ? <ChevronUp className="h-3.5 w-3.5" />
              : <Play className="h-3.5 w-3.5" />}
          </button>
          <span
            className={cn(
              'truncate font-medium',
              isPlayerOpen ? 'max-w-[10rem] text-slate-600' : '',
              // Expired items read as muted/struck so the list communicates
              // at a glance which recordings the engine is currently skipping.
              availabilityStatus(item.available_from, item.available_until) === 'expired'
                ? 'text-slate-400 line-through'
                : '',
            )}
            title={item.recording_name || '(untitled)'}
          >
            {item.recording_name || '(untitled)'}
          </span>
          {isPlayerOpen && (
            // Fixed width keeps the player visually consistent regardless of
            // how wide the folder cell happens to be (varies with viewport
            // and recording-name length).
            <div className="w-72 shrink-0 ml-2">
              <AudioPlayerInRow recordingId={item.recording_id} />
            </div>
          )}
        </div>
      </div>

      {/* Duration */}
      <div className="px-3 py-2.5 tabular-nums text-xs text-muted-foreground">
        {durationLabel(item.recording_duration_ms)}
      </div>

      {/* Schedule status chip */}
      <div className="px-3 py-2.5 flex items-center">
        <ItemStatusChip from={item.available_from} until={item.available_until} />
      </div>

      {/* Actions: schedule + remove */}
      <div className="flex items-center justify-end gap-0.5 pr-2 py-2">
        {canMutate && (
          <ScheduleItemPopover
            item={item}
            pending={schedulePending}
            highlighted={scheduled}
            onSave={(from, until) => onSchedule(item.id, from, until)}
          />
        )}
        {canMutate && (
          /* FolderMinus (not a trash can) + explicit tooltip: this action
             only takes the recording OUT of the folder — it never deletes
             the recording itself. Deleting lives in the bulk bar and the
             recordings library. */
          <Button
            size="sm"
            variant="ghost"
            onClick={onRemove}
            disabled={removePending}
            aria-label="Remove from folder"
            title="Remove from folder — the recording is not deleted"
            className="h-7 w-7 p-0"
          >
            <FolderMinus className="h-3.5 w-3.5 text-slate-400" />
          </Button>
        )}
      </div>
    </li>
  );
}

// ── Per-item schedule popover ─────────────────────────────────────────────────
// A small calendar/clock button per row opens a popover with the go-live and
// expire datetime-local inputs, keeping the row uncluttered. Values are shown
// in the user's local timezone and converted to UTC on save.

function ScheduleItemPopover({
  item, pending, highlighted, onSave,
}: {
  item: RecordingFolderItemRow;
  pending: boolean;
  highlighted: boolean;
  onSave: (availableFrom: string | null, availableUntil: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(() => isoToLocalInput(item.available_from));
  const [until, setUntil] = useState(() => isoToLocalInput(item.available_until));
  const [error, setError] = useState('');

  // Re-seed the inputs whenever the popover opens or the persisted values
  // change underneath us (e.g. after another save invalidates the query).
  useEffect(() => {
    if (open) {
      setFrom(isoToLocalInput(item.available_from));
      setUntil(isoToLocalInput(item.available_until));
      setError('');
    }
  }, [open, item.available_from, item.available_until]);

  function handleSave() {
    const fromIso = localInputToIso(from);
    const untilIso = localInputToIso(until);
    // Guard the same invariant the DB CHECK enforces, so the user gets an
    // inline message instead of a 500/400 round-trip.
    if (fromIso && untilIso && new Date(fromIso).getTime() > new Date(untilIso).getTime()) {
      setError('Go-live must be before expire.');
      return;
    }
    onSave(fromIso, untilIso);
    setOpen(false);
  }

  function handleClear() {
    setFrom('');
    setUntil('');
    setError('');
    onSave(null, null);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          aria-label="Schedule availability"
          className={cn(
            'h-7 w-7 p-0',
            highlighted ? 'text-indigo-600' : 'text-slate-400',
          )}
        >
          <CalendarClock className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 space-y-3">
        <div>
          <p className="text-xs font-semibold text-slate-700">Availability window</p>
          <p className="text-[11px] text-slate-400 leading-snug">
            Outside this window the recording stays in the folder but callers
            don&rsquo;t hear it. Leave a field blank for no limit. Times are in
            your local timezone.
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Go-live</Label>
          {/* In-house calendar (same widget as Time Frames) — never the
              browser-native datetime-local. New dates default to 12:00 am. */}
          <DateTimePicker
            value={from}
            onChange={(v) => { setFrom(v); setError(''); }}
            defaultTimeOnDateSelect="00:00"
            placeholder="No go-live limit"
            className="h-8 text-xs w-full justify-start"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Expire</Label>
          {/* New dates default to 11:59 pm so "expire on the 20th" means
              end-of-day. */}
          <DateTimePicker
            value={until}
            onChange={(v) => { setUntil(v); setError(''); }}
            defaultTimeOnDateSelect="23:59"
            placeholder="No expiry limit"
            className="h-8 text-xs w-full justify-start"
          />
        </div>
        {error && <p role="alert" className="text-[11px] text-red-500">{error}</p>}
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-xs text-slate-500"
            disabled={pending || (!from && !until)}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={pending}
            onClick={handleSave}
          >
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Inline existing-recording picker (inside popover) ─────────────────────────

function AddExistingInline({
  folderId, onAdded,
}: {
  folderId: string;
  onAdded: () => void;
}) {
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  async function handleAdd() {
    if (!recordingId) return;
    setAdding(true);
    setError('');
    try {
      await recordingFolders.addItem(folderId, { recording_id: recordingId });
      setRecordingId(null);
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Add failed.');
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="space-y-2">
      <RecordingPicker
        value={recordingId}
        onChange={setRecordingId}
        label=""
      />
      {error && <p role="alert" className="text-xs text-red-500">{error}</p>}
      <Button
        size="sm"
        className="w-full"
        disabled={!recordingId || adding}
        onClick={handleAdd}
        type="button"
      >
        {adding ? 'Adding…' : 'Add to folder'}
      </Button>
    </div>
  );
}

// ── Folder settings panel ─────────────────────────────────────────────────────

function FolderSettings({
  folder, canMutate, onSaved, onDeleted,
}: {
  folder: RecordingFolderRow;
  canMutate: boolean;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const effectiveCustomerId = useEffectiveCustomerId();
  const [name, setName] = useState(folder.name);
  const [description, setDescription] = useState(folder.description ?? '');
  const [notifyListId, setNotifyListId] = useState<string | null>(folder.notify_sms_list_id ?? null);
  const [notifyTemplate, setNotifyTemplate] = useState(folder.notify_sms_template ?? '');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const update = useMutation({
    mutationFn: () => {
      // Only send fields that actually changed — keeps the audit log
      // tight and avoids touching the description column when only the
      // name was edited (and vice versa).
      const patch: {
        name?: string; description?: string | null;
        notify_sms_list_id?: string | null; notify_sms_template?: string | null;
      } = {};
      if (name.trim() !== folder.name) patch.name = name.trim();
      const trimmedDesc = description.trim();
      const currentDesc = folder.description ?? '';
      if (trimmedDesc !== currentDesc) {
        patch.description = trimmedDesc === '' ? null : trimmedDesc;
      }
      // SMS folder-notify (migration 0117): send only on change so an
      // untouched save never clears the linkage.
      if (notifyListId !== (folder.notify_sms_list_id ?? null)) {
        patch.notify_sms_list_id = notifyListId;
      }
      const trimmedTpl = notifyTemplate.trim();
      if (trimmedTpl !== (folder.notify_sms_template ?? '')) {
        patch.notify_sms_template = trimmedTpl === '' ? null : trimmedTpl;
      }
      return recordingFolders.update(folder.id, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['allFolders', effectiveCustomerId] });
      qc.invalidateQueries({ queryKey: ['folder', folder.id] });
      setSaveError(null);
      onSaved();
    },
    onError: (err: unknown) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
    },
  });

  const deleteFolder = useMutation({
    mutationFn: () => recordingFolders.remove(folder.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['allFolders', effectiveCustomerId] });
      onDeleted();
    },
  });

  const isDirty =
    name.trim() !== folder.name
    || description.trim() !== (folder.description ?? '')
    || notifyListId !== (folder.notify_sms_list_id ?? null)
    || notifyTemplate.trim() !== (folder.notify_sms_template ?? '');

  if (!canMutate) {
    return (
      <div className="text-xs text-muted-foreground">
        You do not have permission to edit this folder.
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-md">
      <div className="space-y-1">
        <Label htmlFor="settings-name">Name</Label>
        <Input
          id="settings-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="settings-description">Description</Label>
        <textarea
          id="settings-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="What goes in this folder…"
          className="block w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400"
        />
        <p className="text-[11px] text-slate-400">
          Optional. Helps team-mates understand the folder's purpose.
        </p>
      </div>

      {/* SMS folder-notify (migration 0117): text a subscriber list every
          time a new recording lands in this folder. */}
      <div className="space-y-2 pt-2 border-t border-border">
        <SmsListPicker
          value={notifyListId}
          onChange={setNotifyListId}
          label="Notify SMS list when a recording is added"
          noneLabel="— Don't send SMS alerts —"
          emptyHint="Create an SMS list first (SMS → Lists) to alert subscribers when a new recording lands here."
        />
        {notifyListId && (
          <div className="space-y-1">
            <Label htmlFor="settings-notify-template">Message template</Label>
            <Input
              id="settings-notify-template"
              value={notifyTemplate}
              onChange={(e) => setNotifyTemplate(e.target.value)}
              maxLength={1600}
              placeholder="New recording in {folder}: {name}"
            />
            <p className="text-[11px] text-slate-400">
              <span className="font-mono">{'{name}'}</span> = recording name,{' '}
              <span className="font-mono">{'{folder}'}</span> = folder name.
              Blank uses the default text. Recipients on the Do-Not-Contact
              list are always skipped.
            </p>
          </div>
        )}
      </div>
      {saveError && <p role="alert" className="text-sm text-destructive">{saveError}</p>}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!isDirty || update.isPending}
          onClick={() => update.mutate()}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      {/* Danger zone */}
      <div className="pt-4 border-t border-border">
        <p className="text-xs font-medium text-red-600 mb-2">Danger zone</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
          onClick={() => setDeleteDialogOpen(true)}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete folder
        </Button>
        <p className="text-[11px] text-slate-400 mt-1">
          Recordings inside will not be deleted — only the folder container is removed.
        </p>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete folder?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            &ldquo;{folder.name}&rdquo; will be deleted. Recordings inside are preserved.
            Any IVR nodes pointing to this folder will lose their target.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={deleteFolder.isPending}>Cancel</Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteFolder.isPending}
              onClick={() => deleteFolder.mutate()}
            >
              {deleteFolder.isPending ? 'Deleting…' : 'Delete folder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
